import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import ClinicAdminLayout from '../components/admin/ClinicAdminLayout';
import { tenantApi } from '../services/api';

const ClinicAdminAntrian = () => {
    const { slug: routeSlug } = useParams();
    const slug =
        routeSlug ||
        (() => {
            try {
                return JSON.parse(localStorage.getItem('user') || '{}')?.klinik?.slug;
            } catch {
                return null;
            }
        })();
    const [data, setData] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const [selectedDate, setSelectedDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('Semua');
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date()); // For calendar navigation

    // State for Modals
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [editForm, setEditForm] = useState({ poli: '', status: '', id_dokter: '', jenis_antrian: '' });

    // Toast Notification
    const [toastNotif, setToastNotif] = useState(null);
    const prevDataRef = useRef();

    useEffect(() => {
        if (prevDataRef.current && prevDataRef.current.length > 0 && data.length > 0) {
            data.forEach(newItem => {
                const oldItem = prevDataRef.current.find(old => old.id === newItem.id);
                if (oldItem && oldItem.rawStatus !== newItem.rawStatus) {
                    const typeColor = newItem.rawStatus === 'selesai' ? 'bg-green-500' : 
                                      newItem.rawStatus === 'dipanggil' ? 'bg-yellow-500' : 'bg-blue-500';
                    const icon = newItem.rawStatus === 'selesai' ? 'material-symbols:check-circle-outline' :
                                 newItem.rawStatus === 'dipanggil' ? 'material-symbols:record-voice-over-outline' : 'material-symbols:info-outline';

                    setToastNotif(`Status Antrian ${newItem.queueNo} (${newItem.name}) berubah menjadi ${newItem.status}`);
                    setTimeout(() => setToastNotif(null), 5000);
                    
                    window.dispatchEvent(new CustomEvent('addNotification', { 
                        detail: { 
                            title: 'Status Antrian Berubah',
                            message: `Status Antrian ${newItem.queueNo} (${newItem.name}) berubah menjadi ${newItem.status}`,
                            type: 'info',
                            color: typeColor,
                            icon: icon
                        } 
                    }));
                }
            });
        }
        prevDataRef.current = data;
    }, [data]);

    useEffect(() => {
        if (!slug) return undefined;
        fetchAntrian(true);
        fetchDoctors();

        const intervalId = setInterval(() => {
            fetchAntrian(false);
        }, 3000);

        return () => clearInterval(intervalId);
    }, [slug]);

    const fetchDoctors = async () => {
        if (!slug) return;
        try {
            const dokterRes = await tenantApi(slug).dokter.getAll();
            if (dokterRes.data?.success) {
                setDoctors(dokterRes.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching doctors:", error);
        }
    };

    const extractErrorMessage = (error, fallback = 'Terjadi kesalahan') => {
        const dataMsg = error?.response?.data?.message;
        if (dataMsg) return dataMsg;
        const dataErrors = error?.response?.data?.errors || error?.response?.data;
        if (dataErrors && typeof dataErrors === 'object') {
            const first = Object.values(dataErrors).flat().find(Boolean);
            if (typeof first === 'string') return first;
        }
        return fallback;
    };

    const resolveAntrianId = (item) => {
        const rawValue = item?.id_antrian ?? item?.rawItem?.id_antrian ?? item?.id ?? item?.rawItem?.id;
        const numericId = Number(rawValue);
        return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
    };

    const normalizeTanggalForApi = (value) => {
        if (!value) return value;
        if (typeof value === 'string' && value.includes('T')) {
            return value.split('T')[0];
        }
        return value;
    };

    const getPoliPrefix = (poliName) => {
        const name = (poliName || "").toLowerCase();
        if (name.includes('umum')) return 'A';
        if (name.includes('gigi')) return 'C';
        if (name.includes('anak')) return 'B';
        return name.charAt(0).toUpperCase() || 'Q';
    };

    const fetchAntrian = async (showLoading = true) => {
        if (!slug) return;
        try {
            if (showLoading) setIsLoading(true);
            const response = await tenantApi(slug).antrian.getAll();
            if (response.data && response.data.success) {
                // Transform data API
                const mappedData = response.data.data.map(item => {
                    const statusName = capitalize(item.status_antrian);
                    const style = getStatusStyle(statusName);
                    
                    const prefix = getPoliPrefix(item.poli?.nama_poli);
                    const indexPart = String(item.nomor_antrian).padStart(2, '0');
                    const queueNumber = `${prefix}${indexPart}`;

                    return {
                        id: resolveAntrianId(item),
                        id_antrian: resolveAntrianId(item),
                        name: item.pasien ? (item.pasien.username || item.pasien.nama_pasien) : 'Pasien Tidak Diketahui',
                        avatarColor: 'bg-cyan-50',
                        source: 'CareLine',
                        queueNo: queueNumber,
                        date: item.tanggal,
                        time: item.jam,
                        poli: item.poli ? item.poli.nama_poli : 'Poli Tidak Diketahui',
                        poli_id: item.id_poli,
                        doctor: item.dokter ? item.dokter.nama_dokter : 'Belum Ditentukan',
                        doctor_id: item.id_dokter,
                        jenis_antrian: item.jenis_antrian === 'bpjs' ? 'BPJS' : 'Umum',
                        status: statusName,
                        rawStatus: item.status_antrian,
                        statusColor: style.statusColor,
                        dotColor: style.dotColor,
                        rawItem: item
                    };
                });
                setData(mappedData);
            }
        } catch (error) {
            console.error("Error fetching antrian:", error);
        } finally {
            if (showLoading) setIsLoading(false);
        }
    };

    const capitalize = (s) => {
        if (!s) return '';
        if (s === 'menunggu') return 'Menunggu';
        if (s === 'dipanggil') return 'Dilayani'; // dipanggil mapped to Dilayani in UI
        if (s === 'selesai') return 'Selesai';
        if (s === 'batal') return 'Batal';
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    // Helper untuk format tanggal dari YYYY-MM-DD ke format tampilan Indonesia
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const options = { day: 'numeric', month: 'long', year: 'numeric' };
            return new Date(dateString).toLocaleDateString('id-ID', options);
        } catch (e) {
            return dateString;
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Selesai':
                return { statusColor: 'bg-[#F1FDFB] text-[#0DBC94]', dotColor: 'bg-[#0DBC94]' };
            case 'Dilayani':
                return { statusColor: 'bg-[#FFF4E5] text-[#FF9800]', dotColor: 'bg-[#FF9800]' };
            case 'Menunggu':
                return { statusColor: 'bg-[#FFF9EA] text-[#FFB016]', dotColor: 'bg-[#FFB016]' };
            default:
                return { statusColor: 'bg-gray-100 text-gray-500', dotColor: 'bg-gray-500' };
        }
    };

    const handleEditClick = (item) => {
        setSelectedItem(item);
        setEditForm({
            poli: item.poli,
            status: item.status,
            id_dokter: item.doctor_id || '',
            jenis_antrian: item.rawItem.jenis_antrian
        });
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setIsDeleteModalOpen(true);
    };

    const handleSaveEdit = async (e) => {
    e.preventDefault();
    const antrianId = resolveAntrianId(selectedItem);
    if (!antrianId) {
        alert('Data antrian tidak valid. Silakan muat ulang halaman.');
        return;
    }

    // 1. Mapping status UI kembali ke format enum database
    let rawStatus = "menunggu";
    if (editForm.status === "Selesai") rawStatus = "selesai";
    if (editForm.status === "Dilayani") rawStatus = "dipanggil";
    if (editForm.status === "Batal") rawStatus = "batal";
    if (editForm.status === "Menunggu") rawStatus = "menunggu";

    // 2. Ambil ID relasi dengan aman
    let rawJam = selectedItem.rawItem.jam || "00:00";
    let safeJam = rawJam.substring(0, 5);

    const payload = {
        status_antrian: rawStatus,
        jenis_antrian: editForm.jenis_antrian,
        id_dokter: editForm.id_dokter ? Number(editForm.id_dokter) : null,
        tanggal: normalizeTanggalForApi(selectedItem.rawItem.tanggal),
        jam: safeJam,
    };
    

    try {
        // 4. Hit API Update
        await tenantApi(slug).antrian.update(antrianId, payload);

        // 5. Jika sukses, tutup modal dan refresh data
        setIsEditModalOpen(false);
        setSelectedItem(null);
        fetchAntrian(false); 
        
    } catch (error) {
        // Tangkap error detail
        const errorMsg = error.response?.data?.message || error.response?.data?.error || "Gagal memperbarui antrian. Cek console.";
        console.error("Payload yang dikirim:", payload); // Sekarang tidak akan error no-undef!
        console.error("Error dari server:", error.response?.data || error);
        alert(errorMsg);
    }
};

    const handleConfirmDelete = async () => {
        const antrianId = resolveAntrianId(selectedItem);
        if (!antrianId) {
            alert('Data antrian tidak valid. Silakan muat ulang halaman.');
            return;
        }
        try {
            await tenantApi(slug).antrian.delete(antrianId);
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
            fetchAntrian(false); // Refresh data from server
        } catch (error) {
            console.error("Error deleting antrian:", error);
            alert(extractErrorMessage(error, "Gagal menghapus antrian"));
        }
    };

    const filteredData = data.filter(item => {
        const matchDate = selectedDate ? item.date === selectedDate : true;
        const matchStatus = filterStatus === 'Semua' ? true : item.status === filterStatus;
        return matchDate && matchStatus;
    });

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedData = [...filteredData].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        return String(a?.[sortConfig.key] ?? '').localeCompare(String(b?.[sortConfig.key] ?? ''), 'id', { numeric: true }) * direction;
    });
    const totalPages = Math.max(1, Math.ceil(sortedData.length / perPage));
    const paginatedData = sortedData.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Calendar Helper Functions
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const generateCalendarDays = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];

        // Previous month filler
        for (let i = 0; i < firstDay; i++) {
            days.push(null);
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };

    const handlePrevMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const handleDateSelect = (date) => {
        if (!date) return;
        const offset = date.getTimezoneOffset();
        const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
        setSelectedDate(adjustedDate.toISOString().split('T')[0]);
        setIsDatePickerOpen(false);
    };

    const monthNames = [
        "Januari", "Februari", "Maret", "April", "Mei", "Juni",
        "Juli", "Agustus", "September", "Oktober", "November", "Desember"
    ];

    if (!slug) {
        return (
            <ClinicAdminLayout activePage="antrian" title="Antrian">
                <p className="text-sm text-gray-500">
                    Slug klinik tidak ditemukan. Gunakan URL <code className="bg-gray-100 px-1 rounded">/nama-klinik/clinic-admin/antrian</code>.
                </p>
            </ClinicAdminLayout>
        );
    }

    return (
        <ClinicAdminLayout activePage="antrian" title="Antrian">
            {/* Antrian Table Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-8">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Antrian</h3>

                    {/* Filters */}
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        {/* Custom Date Picker */}
                        <div className="relative">
                            <button
                                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                                className="flex items-center gap-3 px-6 py-2.5 bg-white border border-gray-200 rounded-full text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/10 focus:border-[#06B6D4] transition-all shadow-sm cursor-pointer min-w-[200px]"
                            >
                                <Icon icon="material-symbols:calendar-month-outline" className="text-lg text-gray-400" />
                                <span>{selectedDate ? formatDateDisplay(selectedDate) : 'Pilih Tanggal'}</span>
                            </button>

                            {isDatePickerOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setIsDatePickerOpen(false)} />
                                    <div className="absolute left-0 mt-2 w-[320px] bg-white rounded-[24px] shadow-2xl border border-gray-100 p-6 z-20 animate-slideDown origin-top-left">
                                        {/* Calendar Header */}
                                        <div className="flex items-center justify-between mb-6">
                                            <h4 className="font-bold text-gray-800">
                                                {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
                                            </h4>
                                            <div className="flex gap-2">
                                                <button onClick={handlePrevMonth} className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 transition-colors">
                                                    <Icon icon="material-symbols:chevron-left" className="text-xl" />
                                                </button>
                                                <button onClick={handleNextMonth} className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 transition-colors">
                                                    <Icon icon="material-symbols:chevron-right" className="text-xl" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Weekdays */}
                                        <div className="grid grid-cols-7 mb-2">
                                            {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(day => (
                                                <div key={day} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest py-2">
                                                    {day}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Days Grid */}
                                        <div className="grid grid-cols-7 gap-1">
                                            {generateCalendarDays().map((date, idx) => (
                                                <button
                                                    key={idx}
                                                    disabled={!date}
                                                    onClick={() => handleDateSelect(date)}
                                                    className={`
                                                        h-9 w-9 flex items-center justify-center rounded-xl text-xs font-bold transition-all
                                                        ${!date ? 'invisible' : 'hover:bg-cyan-50 hover:text-[#06B6D4] text-gray-600'}
                                                        ${date && selectedDate === date.toISOString().split('T')[0] ? 'bg-[#06B6D4] text-white shadow-lg shadow-cyan-500/30' : ''}
                                                    `}
                                                >
                                                    {date?.getDate()}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Footer */}
                                        <div className="mt-6 pt-4 border-t border-gray-50 flex items-center justify-between">
                                            <button
                                                onClick={() => {
                                                    setSelectedDate('');
                                                    setIsDatePickerOpen(false);
                                                }}
                                                className="text-xs font-bold text-gray-400 hover:text-red-500 transition-colors"
                                            >
                                                Clear
                                            </button>
                                            <button
                                                onClick={() => handleDateSelect(new Date())}
                                                className="text-xs font-bold text-[#06B6D4] hover:underline"
                                            >
                                                Hari Ini
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                                className="flex items-center justify-between gap-3 px-6 py-2.5 bg-white border border-gray-200 rounded-full text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/10 focus:border-[#06B6D4] transition-all shadow-sm cursor-pointer min-w-[150px]"
                            >
                                <span>{filterStatus === 'Semua' ? 'Semua Status' : filterStatus}</span>
                                <Icon
                                    icon="material-symbols:keyboard-arrow-down"
                                    className={`text-gray-400 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </button>

                            {/* Custom Dropdown Menu */}
                            {isStatusDropdownOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setIsStatusDropdownOpen(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-full min-w-[160px] bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-20 animate-slideDown origin-top-right overflow-hidden">
                                        {['Semua', 'Selesai', 'Dilayani', 'Menunggu'].map((status) => (
                                            <button
                                                key={status}
                                                className={`w-full text-left px-5 py-2.5 text-sm transition-colors hover:bg-gray-50 ${filterStatus === status ? 'text-[#06B6D4] font-bold bg-cyan-50/50' : 'text-gray-600 font-medium'}`}
                                                onClick={() => {
                                                    setFilterStatus(status);
                                                    setIsStatusDropdownOpen(false);
                                                }}
                                            >
                                                {status === 'Semua' ? 'Semua Status' : status}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => handleSort('queueNo')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">No. Antrian</th>
                                <th onClick={() => handleSort('name')} className="cursor-pointer px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nama Pasien</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Poliklinik</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Jenis</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Dokter</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 font-body">
                            {paginatedData.length > 0 ? (
                                paginatedData.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                                        <td className="px-6 py-5">
                                            <div className="w-fit px-6 py-2 text-white font-bold rounded-2xl text-sm shadow-lg bg-gradient-to-r from-[#06B6D4] to-[#38BFD8]">
                                                {item.queueNo}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 ${item.avatarColor} rounded-full flex-shrink-0 border border-gray-50 flex items-center justify-center text-[10px] font-bold text-cyan-600`}>
                                                    {item.name.charAt(0)}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-sm text-gray-700">{item.name}</span>
                                                    <span className="text-[10px] text-gray-400">{item.date} • {item.time}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">{item.poli}</td>
                                        <td className="px-6 py-5 text-sm font-medium text-gray-500">
                                            <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${item.jenis_antrian === 'BPJS' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                                                {item.jenis_antrian}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 text-sm font-medium text-[#06B6D4]">{item.doctor}</td>
                                        <td className="px-6 py-5">
                                            <div className={`flex items-center justify-center gap-2 h-7 ${item.statusColor} text-[10px] font-bold rounded-lg w-[100px]`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${item.dotColor}`}></div>
                                                <span>{item.status}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handleEditClick(item)}
                                                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-[#06B6D4] hover:border-[#06B6D4] transition-all"
                                                >
                                                    <Icon icon="material-symbols:edit-note-outline" className="text-xl" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(item)}
                                                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-500 transition-all"
                                                >
                                                    <Icon icon="material-symbols:delete-outline" className="text-xl" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500 text-sm">
                                        Tidak ada data yang ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{currentPage}/{totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>

            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={() => setIsEditModalOpen(false)}></div>
                    <div className="bg-white rounded-[32px] w-full max-w-sm p-8 relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Edit Antrian</h3>
                            <button onClick={() => setIsEditModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                                <Icon icon="material-symbols:close" className="text-gray-400 text-xl" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveEdit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Poliklinik</label>
                                <div className="px-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm text-gray-500 font-bold">
                                    {editForm.poli}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Assign Dokter</label>
                                <select
                                    value={editForm.id_dokter}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, id_dokter: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all cursor-pointer"
                                >
                                    <option value="">Pilih Dokter</option>
                                    {doctors.map(doc => (
                                        <option key={doc.id_dokter} value={doc.id_dokter}>{doc.nama_dokter}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Jenis Antrian</label>
                                <select
                                    value={editForm.jenis_antrian}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, jenis_antrian: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all cursor-pointer"
                                >
                                    <option value="umum">Umum</option>
                                    <option value="bpjs">BPJS</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                                <select
                                    value={editForm.status}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all cursor-pointer"
                                >
                                    <option value="Selesai">Selesai</option>
                                    <option value="Dilayani">Dilayani</option>
                                    <option value="Menunggu">Menunggu</option>
                                    <option value="Batal">Batal</option>
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 mt-4 bg-[#06B6D4] text-white font-bold rounded-xl shadow-lg shadow-cyan-500/20 hover:bg-[#038DA5] transition-all active:scale-[0.98]"
                            >
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={() => setIsDeleteModalOpen(false)}></div>
                    <div className="bg-white rounded-[40px] w-full max-w-sm p-10 relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300 flex flex-col items-center text-center">
                        <div className="w-24 h-24 bg-[#FF6B6B] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                            <span className="text-white text-5xl font-bold leading-none -mt-1">!</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Hapus Antrian?</h3>
                        <p className="text-gray-500 text-sm font-medium mb-10 leading-relaxed px-4">
                            Apakah anda yakin ingin menghapus antrian <strong>{selectedItem?.queueNo}</strong>?
                        </p>
                        <div className="w-full space-y-4">
                            <button
                                onClick={handleConfirmDelete}
                                className="w-full py-4 bg-[#FF6B6B] text-white font-bold rounded-2xl shadow-lg shadow-red-500/20 hover:bg-[#ff5252] transition-colors active:scale-95"
                            >
                                Ya, Hapus
                            </button>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="w-full py-4 bg-white text-gray-500 font-bold rounded-2xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors active:scale-95"
                            >
                                Batal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toastNotif && (
                <div className="fixed bottom-8 right-8 z-[200] max-w-sm w-full bg-white rounded-2xl shadow-2xl border border-blue-50/50 p-4 font-body animate-slideUp flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#E1F7FC] flex items-center justify-center flex-shrink-0">
                        <Icon icon="material-symbols:notifications-active-outline" className="text-xl text-[#06B6D4]" />
                    </div>
                    <div className="flex-1 mt-0.5">
                        <h4 className="text-sm font-bold text-gray-800 mb-1">Pembaruan Antrian</h4>
                        <p className="text-xs font-medium text-gray-500 leading-relaxed">
                            {toastNotif}
                        </p>
                    </div>
                    <button onClick={() => setToastNotif(null)} className="text-gray-400 hover:text-gray-700 transition-colors">
                        <Icon icon="material-symbols:close" className="text-lg" />
                    </button>
                    
                    {/* Auto-dismiss progress bar effect (purely visual) */}
                    <div className="absolute bottom-0 left-0 h-1 bg-[#06B6D4] rounded-b-2xl animate-[shrink_5s_linear_forwards]"></div>
                </div>
            )}
            <style>{`
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>
        </ClinicAdminLayout>
    );
};

export default ClinicAdminAntrian;
